/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { CheckCircle2, Loader2, Settings2, Stamp, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import api from '@/lib/api';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import { fmt } from './helpers';

/* ------------------------------------------------------------------ TSA config */
export const TsaConfigCard = ({ cfg, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ preset: 'digicert', url: '', username: '', password: '' });
  const { run, busy } = useAsyncAction();
  useEffect(() => { if (cfg) setForm({ preset: cfg.preset, url: cfg.url || '', username: cfg.username || '', password: '' }); }, [cfg]);
  const preset = cfg?.presets?.find((p) => p.id === form.preset);
  const [testResult, setTestResult] = useState(null);
  const { run: runTest, busy: testing } = useAsyncAction();
  const testTsa = () => runTest(async () => {
    const r = await api.post('/legal/timestamp/test');
    setTestResult(r.data);
    if (!r.data.ok) throw new Error(r.data.error || 'TSA non raggiungibile');
    return r.data;
  }, { success: (d) => `TSA operativa · ${d.signer_cn || d.url} · ${d.latency_ms} ms` });
  const save = () => run(async () => {
    const r = await api.put('/legal/timestamp/config', { preset: form.preset, url: form.preset === 'custom' ? form.url : (form.url || undefined), username: form.username || undefined, password: form.password || undefined });
    onSaved?.(r.data); setOpen(false);
  }, { success: 'Provider di marcatura temporale salvato' });

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span data-testid="tsa-current">
          TSA attiva: <b className="text-foreground">{cfg?.label || '…'}</b>
          {cfg && (cfg.qualified
            ? <Badge className="ml-2 rounded-full bg-primary/10 text-primary hover:bg-primary/10">eIDAS</Badge>
            : <Badge variant="outline" className="ml-2 rounded-full">RFC 3161 · non qualificata</Badge>)}
        </span>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8" data-testid="tsa-config-toggle"><Settings2 className="h-3.5 w-3.5" /> Configura TSA</Button>
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent>
        <Card className="rounded-2xl mt-2" data-testid="tsa-config-card">
          <CardContent className="p-4 grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label>Provider</Label>
              <Select value={form.preset} onValueChange={(v) => setForm({ ...form, preset: v, url: v === 'custom' ? form.url : '' })}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="tsa-preset-select"><SelectValue /></SelectTrigger>
                <SelectContent>{(cfg?.presets || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
              {preset && <p className="text-xs text-muted-foreground mt-1">{preset.note}</p>}
            </div>
            {form.preset === 'custom' && (
              <>
                <div className="sm:col-span-2"><Label>URL RFC 3161 del QTSP</Label><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://tsa.qtsp.it/tsa" className="h-11 rounded-xl font-mono text-xs" data-testid="tsa-url" /></div>
                <div><Label>Utente (Basic Auth)</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} autoComplete="off" className="h-11 rounded-xl" data-testid="tsa-user" /></div>
                <div><Label>Password {cfg?.has_password && <span className="text-muted-foreground font-normal">(salvata, cifrata)</span>}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="new-password" placeholder={cfg?.has_password ? 'Lascia vuoto per non cambiarla' : ''} className="h-11 rounded-xl" data-testid="tsa-password" /></div>
              </>
            )}
            <div className="sm:col-span-2 flex flex-wrap justify-between items-center gap-2">
              <Button variant="outline" className="rounded-xl h-10" onClick={testTsa} disabled={testing || busy} data-testid="tsa-test-btn">{testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Stamp className="h-4 w-4" />} Prova TSA (marca di test)</Button>
              <Button className="rounded-xl h-10" onClick={save} disabled={busy} data-testid="tsa-save-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Salva provider</Button>
            </div>
            {testResult && (
              <div className={`sm:col-span-2 rounded-xl border p-3 text-xs ${testResult.ok ? 'border-primary/40 bg-primary/5' : 'border-destructive/40 bg-destructive/5'}`} data-testid="tsa-test-result" data-ok={testResult.ok ? 'true' : 'false'}>
                <div className={`flex items-center gap-2 font-semibold ${testResult.ok ? 'text-primary' : 'text-destructive'}`}>
                  {testResult.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                  {testResult.ok ? `TSA operativa: ${testResult.signer_cn || testResult.tsa_name || testResult.url}` : 'TSA non operativa'}
                </div>
                <div className="mt-1 text-foreground/90 break-words">
                  {testResult.ok
                    ? <>Marca di prova emessa il {fmt(testResult.gen_time)} · seriale <span className="font-mono">{testResult.serial_number}</span> · {testResult.latency_ms} ms · {testResult.qualified ? 'provider qualificato eIDAS' : 'RFC 3161 non qualificata'}</>
                    : testResult.error}
                </div>
                <div className="text-muted-foreground mt-1">Endpoint: <span className="font-mono">{testResult.url}</span>{testResult.has_auth ? ' · autenticazione Basic attiva' : ''}</div>
              </div>
            )}
          </CardContent>
        </Card>
      </CollapsibleContent>
    </Collapsible>
  );
};
